import { generateEncryptionKey } from '@/infra/crypto/core'
import type { EncryptionKey, KeyRing } from './types'

export const createKeyRing = async (userEmail: string): Promise<KeyRing> => {
  const key = await generateEncryptionKey()
  const now = new Date().toISOString()

  const firstKey: EncryptionKey = {
    version: 1,
    key,
    createdAt: now,
  }

  return {
    userEmail,
    keys: [firstKey],
    updatedAt: now,
  }
}

export const addKey = async (keyRing: KeyRing): Promise<KeyRing> => {
  const activeKey = getActiveKey(keyRing)
  const nextVersion = activeKey ? activeKey.version + 1 : 1

  if (keyRing.keys.some((k) => k.version === nextVersion)) {
    throw new Error(`Key version collision: version ${String(nextVersion)} already exists`)
  }

  const key = await generateEncryptionKey()
  const now = new Date().toISOString()

  const newKey: EncryptionKey = {
    version: nextVersion,
    key,
    createdAt: now,
  }

  return {
    ...keyRing,
    keys: [...keyRing.keys, newKey],
    updatedAt: now,
  }
}

export const getKeyByVersion = (keyRing: KeyRing, version: number): EncryptionKey | null => {
  return keyRing.keys.find((k) => k.version === version) ?? null
}

export const getActiveKey = (keyRing: KeyRing): EncryptionKey | null => {
  if (keyRing.keys.length === 0) return null

  return keyRing.keys.reduce<EncryptionKey | null>((latest, current) => {
    if (!latest || current.version > latest.version) return current
    return latest
  }, null)
}

export const rotateKey = async (keyRing: KeyRing): Promise<KeyRing> => {
  return addKey(keyRing)
}
